import { useEffect, useState, useCallback } from 'react';
import {
  Key,
  Plus,
  Copy,
  Eye,
  EyeOff,
  Trash2,
  RefreshCw,
  Check,
  AlertCircle,
  Loader2,
  Clock,
  Shield,
} from 'lucide-react';
import { apiKeysApi, ApiKey } from '../services/api';
import clsx from 'clsx';

interface CreateKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (key: ApiKey) => void;
}

function CreateKeyModal({ isOpen, onClose, onCreated }: CreateKeyModalProps) {
  const [name, setName] = useState('');
  const [expiresIn, setExpiresIn] = useState<'30d' | '90d' | '1y' | 'never'>('never');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { apiKey } = await apiKeysApi.create({ 
        name, 
        expiresIn,
      });
      onCreated(apiKey);
      setName('');
      setExpiresIn('never');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create API key');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal">
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-content p-6 relative">
        <h2 className="text-lg font-semibold text-foreground mb-4">Create API Key</h2>

        {error && (
          <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="keyName" className="label">
              Key Name
            </label>
            <input
              id="keyName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder="Production API Key"
              required
            />
          </div>

          <div>
            <label className="label">Expiration</label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { value: '30d', label: '30 days' },
                { value: '90d', label: '90 days' },
                { value: '1y', label: '1 year' },
                { value: 'never', label: 'Never' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setExpiresIn(option.value as typeof expiresIn)}
                  className={clsx(
                    'px-3 py-2 text-sm font-medium rounded-md transition-colors',
                    expiresIn === option.value
                      ? 'bg-accent text-white'
                      : 'bg-background-tertiary text-foreground-secondary hover:text-foreground'
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !name}
              className="btn-primary flex-1"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Create Key'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface NewKeyDisplayProps {
  apiKey: ApiKey;
  onDismiss: () => void;
}

function NewKeyDisplay({ apiKey, onDismiss }: NewKeyDisplayProps) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = useCallback(async () => {
    if (apiKey.key) {
      await navigator.clipboard.writeText(apiKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [apiKey.key]);

  return (
    <div className="card p-6 border-success/30 bg-success/5 animate-slide-down">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center flex-shrink-0">
          <Key className="w-5 h-5 text-success" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-foreground font-medium">API Key Created</h3>
          <p className="text-sm text-foreground-secondary mt-1">
            Make sure to copy your API key now. You won't be able to see it again!
          </p>

          <div className="mt-4 p-3 bg-background-tertiary rounded-lg font-mono text-sm break-all">
            {apiKey.key}
          </div>

          <div className="mt-4 flex gap-3">
            <button onClick={copyToClipboard} className="btn-primary">
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy Key
                </>
              )}
            </button>
            <button onClick={onDismiss} className="btn-secondary">
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ApiKeyRowProps {
  apiKey: ApiKey;
  onRevoke: (id: string) => void;
  onRotate: (id: string) => void;
  isLoading: boolean;
}

function ApiKeyRow({ apiKey, onRevoke, onRotate, isLoading }: ApiKeyRowProps) {
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyToClipboard = useCallback(async () => {
    await navigator.clipboard.writeText(apiKey.keyPrefix + '••••••••••••••••••••••••••••');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [apiKey.keyPrefix]);

  const maskedKey = apiKey.keyPrefix + '••••••••••••••••••••••••••••';

  return (
    <tr className={clsx(apiKey.isExpired && 'opacity-50')}>
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-background-tertiary flex items-center justify-center">
            <Key className="w-4 h-4 text-foreground-secondary" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">{apiKey.name}</p>
            <p className="text-xs text-foreground-tertiary">
              Created {new Date(apiKey.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
      </td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-2">
          <code className="font-mono text-sm text-foreground-secondary">
            {showKey ? maskedKey : apiKey.keyPrefix + '••••••••'}
          </code>
          <button
            onClick={() => setShowKey(!showKey)}
            className="p-1 text-foreground-muted hover:text-foreground-secondary transition-colors"
          >
            {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
          <button
            onClick={copyToClipboard}
            className="p-1 text-foreground-muted hover:text-foreground-secondary transition-colors"
          >
            {copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </td>
      <td className="py-4 px-4 hidden sm:table-cell">
        {apiKey.isExpired ? (
          <span className="badge-error">Expired</span>
        ) : apiKey.expiresAt ? (
          <div className="flex items-center gap-1.5 text-xs text-foreground-tertiary">
            <Clock className="w-3.5 h-3.5" />
            {new Date(apiKey.expiresAt).toLocaleDateString()}
          </div>
        ) : (
          <span className="text-xs text-foreground-tertiary">Never</span>
        )}
      </td>
      <td className="py-4 px-4 hidden md:table-cell">
        <span className="text-xs text-foreground-tertiary">
          {apiKey.lastUsedAt
            ? new Date(apiKey.lastUsedAt).toLocaleDateString()
            : 'Never'}
        </span>
      </td>
      <td className="py-4 px-4 text-right">
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => onRotate(apiKey.id)}
            disabled={isLoading}
            className="p-1.5 text-foreground-muted hover:text-foreground-secondary transition-colors"
            title="Rotate key"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => onRevoke(apiKey.id)}
            disabled={isLoading}
            className="p-1.5 text-foreground-muted hover:text-error transition-colors"
            title="Revoke key"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export function ApiKeysPage() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKey, setNewKey] = useState<ApiKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchApiKeys = useCallback(async () => {
    try {
      const { apiKeys } = await apiKeysApi.list();
      setApiKeys(apiKeys);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const handleCreateKey = (key: ApiKey) => {
    setShowCreateModal(false);
    setNewKey(key);
    fetchApiKeys();
  };

  const handleRevoke = async (id: string) => {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    setActionLoading(id);
    try {
      const response = await apiKeysApi.revoke(id);
      
      // Show sync status in success message
      if (response?.syncStatus) {
        const { local, infrastructure, gpuContainer } = response.syncStatus;
        if (local && infrastructure) {
          console.log(`✓ API key revoked locally and synced to GPU infrastructure${gpuContainer ? ` (${gpuContainer})` : ''}`);
        } else if (local && !infrastructure) {
          console.warn('⚠ API key revoked locally, but sync to GPU infrastructure may have failed. Please verify in admin dashboard.');
        }
      }
      
      fetchApiKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke API key');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRotate = async (id: string) => {
    if (!confirm('Rotating this key will invalidate the current key and generate a new one. Continue?')) {
      return;
    }

    setActionLoading(id);
    try {
      const { apiKey } = await apiKeysApi.rotate(id);
      setNewKey(apiKey);
      fetchApiKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rotate API key');
    } finally {
      setActionLoading(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-32 skeleton rounded" />
        <div className="h-64 skeleton rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">API Keys</h1>
          <p className="text-foreground-secondary mt-1 text-sm">
            Manage your API keys for programmatic access
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          Create Key
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="p-4 bg-error/10 border border-error/20 rounded-lg flex items-center gap-3 text-sm text-error animate-slide-down">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-auto text-error hover:text-error/80"
          >
            ×
          </button>
        </div>
      )}

      {/* New key display */}
      {newKey && (
        <NewKeyDisplay
          apiKey={newKey}
          onDismiss={() => setNewKey(null)}
        />
      )}

      {/* Security notice */}
      <div className="card p-4 flex items-start gap-3 bg-accent/5 border-accent/20">
        <Shield className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-foreground">
            Keep your API keys secure
          </p>
          <p className="text-xs text-foreground-secondary mt-1">
            Never share your API keys in public repositories, client-side code, or with untrusted parties.
            Rotate keys regularly and revoke any compromised keys immediately.
          </p>
        </div>
      </div>

      {/* API Keys table */}
      <div className="card overflow-hidden">
        {apiKeys.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Key</th>
                  <th className="hidden sm:table-cell">Expires</th>
                  <th className="hidden md:table-cell">Last Used</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((key) => (
                  <ApiKeyRow
                    key={key.id}
                    apiKey={key}
                    onRevoke={handleRevoke}
                    onRotate={handleRotate}
                    isLoading={actionLoading === key.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <Key className="w-10 h-10 text-foreground-muted mx-auto mb-4" />
            <p className="text-foreground-secondary">No API keys yet</p>
            <p className="text-sm text-foreground-tertiary mt-1">
              Create your first API key to start using the platform programmatically
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary mt-4"
            >
              <Plus className="w-4 h-4" />
              Create API Key
            </button>
          </div>
        )}
      </div>

      {/* Create key modal */}
      <CreateKeyModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleCreateKey}
      />
    </div>
  );
}
